// A-4 (Code-Review 2026-05-29) — React error boundary for the public viewer.
//
// public/ previously had NO error boundary, so a single render-time throw in
// the shared renderer (e.g. the A-4 credits null-deref, or any future bad
// slide shape) unmounted the entire React tree → blank SSR page on the primary
// content surface. This boundary catches such throws and shows a readable,
// recoverable error card instead of a white screen.
//
// Placement is per-scrolly-route: ScrollyViewerPage wraps its body in this
// boundary, keyed on the scrolly identifier, so a broken scrolly fails in
// isolation while the rest of the site (home, category, creator) keeps working
// and navigating to a different scrolly resets the boundary (clientRouting is
// on — see pages/+config.ts).
//
// The fallback is deliberately self-contained: no hooks, no AppShell, no
// context reads — only inline styles + token string-constants. As the last
// line of defense against a white screen, the boundary's own render must never
// be able to throw.

import { Component, type ErrorInfo, type ReactNode } from "react";
import { FONT_DISPLAY, FONT_UI, PINK } from "../../lib/tokens";

interface Props {
  children: ReactNode;
  /** Optional identifier (slug/publicKey) included in the crash log. */
  context?: string;
  /**
   * When this value changes the boundary clears any caught error and re-renders
   * its children. Pass the scrolly identifier so client-side navigation from a
   * crashed scrolly to a working one recovers — without forcing a remount of
   * the renderer on every navigation (which a React `key` would).
   */
  resetKey?: string;
}

interface State {
  error: Error | null;
  prevResetKey?: string;
}

export class ScrollyErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    if (props.resetKey !== state.prevResetKey) {
      // Scrolly changed (or first mount) → drop any stale error so the new
      // children get a clean render attempt.
      return { error: null, prevResetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface the crash instead of swallowing it — a silently-caught
    // white-screen is as hard to diagnose as the crash itself.
    console.error(
      `[ScrollyErrorBoundary] render crashed${this.props.context ? ` (${this.props.context})` : ""}:`,
      error,
      info.componentStack,
    );
  }

  private handleRetry = (): void => {
    if (typeof window !== "undefined") window.location.reload();
  };

  render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        style={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: "40px",
          background: "#FAFAF8",
          color: "#1A1A1E",
          fontFamily: FONT_UI,
        }}
      >
        <div style={{ maxWidth: 560 }}>
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: PINK,
              letterSpacing: 1,
              textTransform: "uppercase",
              marginBottom: 10,
            }}
          >
            Anzeige-Fehler
          </div>
          <h1
            style={{
              fontFamily: FONT_DISPLAY,
              fontSize: 48,
              fontWeight: 500,
              margin: 0,
              letterSpacing: -1,
              lineHeight: 1.05,
            }}
          >
            Dieser Scrolly konnte{" "}
            <em style={{ color: PINK, fontStyle: "italic" }}>nicht angezeigt</em>{" "}
            werden.
          </h1>
          <p
            style={{
              color: "#55555B",
              fontSize: 16,
              lineHeight: 1.5,
              marginTop: 20,
            }}
          >
            Beim Aufbau der Story ist ein Fehler aufgetreten. Bitte lade die
            Seite neu — andere Scrollys sind nicht betroffen.
          </p>
          {import.meta.env.DEV && (
            <pre
              style={{
                marginTop: 20,
                padding: 12,
                background: "#F0F0EE",
                borderRadius: 8,
                fontSize: 12,
                textAlign: "left",
                overflowX: "auto",
                color: "#9B1C3D",
              }}
            >
              {error.message}
            </pre>
          )}
          <div
            style={{
              marginTop: 28,
              display: "flex",
              justifyContent: "center",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={this.handleRetry}
              type="button"
              style={{
                background: PINK,
                color: "#fff",
                padding: "11px 24px",
                borderRadius: 999,
                fontSize: 14,
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
              }}
            >
              Erneut versuchen
            </button>
            <a
              href="/"
              style={{
                display: "inline-block",
                background: "transparent",
                color: "#1A1A1E",
                padding: "11px 24px",
                borderRadius: 999,
                fontSize: 14,
                fontWeight: 600,
                textDecoration: "none",
                border: "1px solid rgba(0,0,0,0.15)",
              }}
            >
              Zur Startseite
            </a>
          </div>
        </div>
      </div>
    );
  }
}
